import { createActor, fromPromise } from "xstate";

import { AuthMachine } from "@recordsure/unity-auth-machine";

import { config } from "@/config";

import type { Router } from "vue-router";
import type { ErrorObject, UserPermission } from "@recordsure/unity-auth-machine";

export let authMachine: ReturnType<typeof setupAuthMachine>;

export function setupAuthMachine(router: Router) {
  const actor = createActor(
    AuthMachine.provide({
      actors: {
        waitForRouter: fromPromise<void>(async () => {
          await router.isReady();
        }),
        isAuthRoute: fromPromise<boolean, { stateKey: string }>(async ({ input }) => {
          if (router.currentRoute.value.name === "auth") {
            const state = localStorage.getItem(input.stateKey);

            // We're calling back into auth but we've no state
            if (!state) throw { code: "STA" } satisfies ErrorObject;

            return true;
          }

          return await Promise.resolve(false);
        }),
        routerRedirect: fromPromise<void, { url: string }>(async ({ input }) => {
          await router.replace(input.url);
        }),
      },
    }),
    { input: { config } },
  ).start();

  authMachine = actor;

  return actor;
}

export type UserPermissionName =
  | "readAccess"
  | "writeAccess"
  | "deleteAccess"
  | "canManageClientConfig"
  | "canAnnotate"
  | "canBookmark"
  | "canClassify"
  | "canCreateBrandFilterSet"
  | "canDeleteCase"
  | "canDeleteFile"
  | "canDownload"
  | "canManageAudit"
  | "canManageThresholds"
  | "canManageUsers"
  | "canPreConvert"
  | "canRedact"
  | "canAuditAccess"
  | "canDownloadMeetingNotes"
  | "canManageAccess"
  | "canManageLenses"
  | "canMigrateRecords"
  | "canSearchAllCases"
  | "canUseCaseHistory"
  | "canUseFind"
  | "canViewAdminPage"
  | "canViewGroupCases"
  | "canSaveFile"
  | "canStartCase"
  | "canUpload"
  | "canViewAudit"
  | "canViewSummariser"
  | "canEditForms"
  | "canSaveFragments"
  | "canManageMeetingNotes"
  | "canAmendCheckpointEvaluations"
  | "canAmendCheckpointDataValues"
  | "canRequestCaseDeletion"
  | "canActionCaseDeletion"
  | "canChangeLegalHoldStatus"
  | "canChangeCaseRetentionDuration"
  | "canUndeleteCases";

export type ViewerUserPermission = UserPermission<UserPermissionName>;
